// Data for the Solutions section.
// Each industry renders its own SEO friendly page at /solutions/:slug from this single source.

export interface Solution {
  slug: string;
  name: string;
  icon: string;
  cardDescription: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  heroEyebrow: string;
  heroTitle: string;
  heroLead: string;
  problem: string;
  challenges: string[];
  benefits: { title: string; description: string }[];
  useCases: string[];
  faqs: { question: string; answer: string }[];
}

export const SOLUTIONS: Solution[] = [
  {
    slug: "banks",
    name: "Banks and credit unions",
    icon: "bank",
    cardDescription: "Block deceased account takeover and new account fraud before funds move.",
    metaTitle: "Death Verification for Banks and Credit Unions",
    metaDescription:
      "Stop deceased account takeover and new account fraud. Verify customers against fresh nationwide death data during onboarding and account reviews.",
    keywords:
      "deceased verification for banks, bank fraud prevention, deceased account takeover, new account fraud, death verification credit union",
    heroEyebrow: "For banks and credit unions",
    heroTitle: "Stop deceased account fraud before funds leave the building",
    heroLead:
      "Fraudsters target dormant and estate accounts of deceased customers, and open new accounts using stolen deceased identities. VerifyDeceased gives your fraud and compliance teams fresh, confirmed death data to catch it early.",
    problem:
      "When a customer passes away, there is often a gap of weeks or months before the death is reflected in the data most institutions rely on. During that window, criminals drain dormant accounts, redirect funds and open new lines in the deceased person's name.",
    challenges: [
      "Dormant and estate accounts drained before the family notifies the bank",
      "New accounts opened with deceased identities that pass standard checks",
      "Slow death data that surfaces months after the fraud is complete",
      "Regulatory and reputational risk from paying out on deceased accounts"
    ],
    benefits: [
      {
        title: "Fresh, confirmed data",
        description:
          "Records are added weekly, often within seven days of passing, so you close the fraud window fast."
      },
      {
        title: "Onboarding and monitoring",
        description:
          "Screen new applicants in real time and re-check your existing book on a schedule."
      },
      {
        title: "Enrichment that helps",
        description:
          "Every match returns date of death, age and last known property address for review and case files."
      }
    ],
    useCases: [
      "Screen new account applications against confirmed deceased records",
      "Run periodic checks on dormant and estate accounts",
      "Flag deceased customers before wire transfers or large withdrawals",
      "Support KYC and account maintenance processes with reliable death data"
    ],
    faqs: [
      {
        question: "How can a bank verify if a customer is deceased?",
        answer:
          "Upload your customer list to VerifyDeceased or call our API during onboarding and account reviews. We match your records against fresh nationwide death data and return confirmed matches with date of death, age and last known property address."
      },
      {
        question: "How fast is the death data for fraud prevention?",
        answer:
          "New confirmed deceased records are added every week, often within seven days of a person passing. This speed is what lets banks catch deceased account fraud before funds move."
      }
    ]
  },
  {
    slug: "lenders",
    name: "Lenders and card issuers",
    icon: "card",
    cardDescription: "Screen every applicant against confirmed deceased identities.",
    metaTitle: "Death Verification for Lenders and Card Issuers",
    metaDescription:
      "Stop credit and loan fraud committed with deceased identities. Screen applicants against nationwide death data during origination and portfolio reviews.",
    keywords:
      "deceased verification for lenders, credit card fraud deceased, loan fraud prevention, deceased identity credit application, death screening lending",
    heroEyebrow: "For lenders and card issuers",
    heroTitle: "Decline credit applications made with deceased identities",
    heroLead:
      "Deceased identities are a favorite tool for synthetic and stolen identity fraud in lending. VerifyDeceased lets you screen every applicant and existing account against confirmed death records.",
    problem:
      "Credit card and loan applications submitted in the name of a deceased person can pass traditional identity checks because the identity itself is real. Without fresh death data, these applications get approved and the losses land on the issuer.",
    challenges: [
      "Card and loan applications approved using deceased identities",
      "Synthetic identities built on real deceased Social Security numbers",
      "Charge offs on accounts that should never have been opened",
      "Manual review teams working without reliable death signals"
    ],
    benefits: [
      {
        title: "Real time origination checks",
        description:
          "Verify an applicant during the application flow with a millisecond API response."
      },
      {
        title: "Portfolio suppression",
        description:
          "Batch check your existing book to find accounts opened before your controls improved."
      },
      {
        title: "Lower charge offs",
        description:
          "Stop funding accounts that will never be repaid because the identity belongs to a deceased person."
      }
    ],
    useCases: [
      "Screen credit card and loan applications at origination",
      "Detect synthetic identities built on deceased identifiers",
      "Suppress deceased accounts across an existing portfolio",
      "Feed death signals into your fraud and underwriting models"
    ],
    faqs: [
      {
        question: "How do lenders detect deceased identity fraud?",
        answer:
          "Lenders integrate VerifyDeceased into origination and portfolio review. We compare applicant details against fresh confirmed death records and return a match with a confidence score so you can decline or review the application."
      },
      {
        question: "Can I verify applicants in real time?",
        answer:
          "Yes. Our REST API returns a verification result in milliseconds so you can screen an applicant without adding friction to the application experience."
      }
    ]
  },
  {
    slug: "employers",
    name: "Employers and background checks",
    icon: "briefcase",
    cardDescription: "Catch deceased identity use in hiring, onboarding and payroll.",
    metaTitle: "Death Verification for Employers and HR Teams",
    metaDescription:
      "Prevent deceased identity fraud in hiring and payroll. Employers and background screeners can confirm identities against nationwide death data.",
    keywords:
      "deceased verification for employers, employment identity fraud, background check death records, deceased identity hiring, payroll fraud prevention",
    heroEyebrow: "For employers and screeners",
    heroTitle: "Keep deceased identities out of your hiring and payroll",
    heroLead:
      "Stolen deceased identities are used to pass employment checks, secure jobs and collect wages. VerifyDeceased adds a fast, reliable death signal to your background screening process.",
    problem:
      "Identity thieves use the details of deceased people to apply for jobs, especially remote roles, and to run payroll and tax fraud. Standard background checks confirm the identity is real but not whether the person is alive.",
    challenges: [
      "Applicants using the identity of a deceased person",
      "Payroll and tax fraud tied to deceased identifiers",
      "Background checks that verify identity but not living status",
      "Onboarding teams without access to fresh death data"
    ],
    benefits: [
      {
        title: "Add to existing checks",
        description:
          "Layer a death signal onto your current background screening with a simple API call or file upload."
      },
      {
        title: "Protect payroll",
        description: "Catch deceased identities before they reach your payroll and tax systems."
      },
      {
        title: "Fast and simple",
        description:
          "Verify a single candidate or a batch and get confirmed results back quickly."
      }
    ],
    useCases: [
      "Screen candidates during background checks",
      "Verify identities before payroll setup",
      "Support I-9 and onboarding integrity",
      "Batch check contractor and vendor rosters"
    ],
    faqs: [
      {
        question: "Why would an employer verify if someone is deceased?",
        answer:
          "Identity thieves use deceased people's details to obtain employment and commit payroll or tax fraud. Adding a death verification step confirms that a candidate's identity does not belong to a deceased person."
      }
    ]
  },
  {
    slug: "insurance",
    name: "Insurance carriers",
    icon: "umbrella",
    cardDescription: "Confirm date of death for claims, policy reviews and audits.",
    metaTitle: "Death Verification for Insurance Carriers",
    metaDescription:
      "Confirm date of death for claims and policy administration. Insurance carriers get nationwide death data to validate claims and prevent fraud.",
    keywords:
      "deceased verification for insurance, death claim verification, insurance fraud death, date of death confirmation, policy administration death data",
    heroEyebrow: "For insurance carriers",
    heroTitle: "Validate claims and policies with confirmed death data",
    heroLead:
      "Whether you are paying a life claim or auditing an annuity book, you need to know the truth about date of death. VerifyDeceased delivers fresh, confirmed records to support claims and policy reviews.",
    problem:
      "Insurers face two problems around death data: paying claims on fraudulent or premature death reports, and continuing to pay annuities or benefits after a policyholder has passed. Both come from unreliable death information.",
    challenges: [
      "Fraudulent or premature death claims",
      "Annuity and benefit overpayments after a policyholder dies",
      "Manual death searches that are slow and incomplete",
      "Audit findings tied to stale death data"
    ],
    benefits: [
      {
        title: "Claims validation",
        description:
          "Confirm date of death before you pay, with an auditable match and confidence score."
      },
      {
        title: "Annuity and benefit audits",
        description: "Batch check your book to find deceased policyholders still receiving payments."
      },
      {
        title: "Nationwide coverage",
        description: "Confirmed records across all 50 states and the District of Columbia."
      }
    ],
    useCases: [
      "Validate life insurance claims before payout",
      "Audit annuity and pension books for deceased recipients",
      "Support special investigation units with reliable death data",
      "Confirm date of death for policy administration"
    ],
    faqs: [
      {
        question: "How do insurers confirm a date of death?",
        answer:
          "Insurers submit claimant or policyholder details to VerifyDeceased and receive a confirmed match with the date of death and a confidence score, either as a batch file or through the API."
      }
    ]
  },
  {
    slug: "government",
    name: "Government and public sector",
    icon: "landmark",
    cardDescription: "Prevent benefit and entitlement payments to deceased recipients.",
    metaTitle: "Death Verification for Government Agencies",
    metaDescription:
      "Prevent improper benefit and entitlement payments to deceased recipients with fresh nationwide death data for government agencies.",
    keywords:
      "deceased verification for government, benefit fraud deceased, improper payments death, entitlement fraud prevention, public sector death data",
    heroEyebrow: "For government and public sector",
    heroTitle: "Stop improper payments to deceased recipients",
    heroLead:
      "Benefit, pension and entitlement programs lose significant funds to payments that continue after a recipient has died. VerifyDeceased gives agencies fresh, confirmed death data to close that gap.",
    problem:
      "Public programs depend on timely death information to stop payments. When death data lags, programs keep paying benefits, pensions and entitlements to accounts belonging to deceased recipients, and recovery is difficult.",
    challenges: [
      "Benefit and pension payments that continue after death",
      "Slow death reporting across jurisdictions",
      "Difficult and costly recovery of improper payments",
      "Audit and oversight pressure to reduce improper payments"
    ],
    benefits: [
      {
        title: "Reduce improper payments",
        description: "Identify deceased recipients before the next payment cycle runs."
      },
      {
        title: "Nationwide coverage",
        description: "Confirmed records across all 50 states and the District of Columbia."
      },
      {
        title: "Batch or API",
        description: "Run large recipient files or integrate checks directly into your systems."
      }
    ],
    useCases: [
      "Screen benefit and entitlement rosters for deceased recipients",
      "Audit pension and payment programs",
      "Support program integrity and oversight teams",
      "Reduce improper and duplicate payments"
    ],
    faqs: [
      {
        question: "How can agencies reduce improper payments to deceased recipients?",
        answer:
          "Agencies periodically match recipient rosters against VerifyDeceased's fresh death data to identify deceased recipients and stop payments before the next cycle, reducing improper payments and recovery costs."
      }
    ]
  },
  {
    slug: "collections",
    name: "Collections and recovery",
    icon: "chart",
    cardDescription: "Suppress deceased accounts before your team makes contact.",
    metaTitle: "Death Verification for Collections Teams",
    metaDescription:
      "Suppress deceased accounts and stay compliant. Collections and recovery teams can identify deceased debtors with fresh nationwide death data.",
    keywords:
      "deceased verification for collections, deceased debtor suppression, collections compliance death, debt recovery death data, deceased account scrub",
    heroEyebrow: "For collections and recovery",
    heroTitle: "Suppress deceased accounts and protect your compliance posture",
    heroLead:
      "Contacting the family of a deceased debtor creates compliance risk and reputational harm. VerifyDeceased lets you scrub your accounts against fresh death data before your team reaches out.",
    problem:
      "Collections teams that contact deceased debtors risk violating consumer protection rules and damaging their brand. Without current death data, deceased accounts stay in active queues and get worked like any other.",
    challenges: [
      "Outreach to accounts where the debtor has passed away",
      "Compliance risk from contacting deceased debtors and their families",
      "Wasted effort working accounts that cannot be recovered normally",
      "Stale death data that leaves deceased accounts in active queues"
    ],
    benefits: [
      {
        title: "Pre contact suppression",
        description: "Scrub queues before outreach so agents never work a deceased account."
      },
      {
        title: "Compliance protection",
        description: "Reduce the risk of contacting deceased debtors and their grieving families."
      },
      {
        title: "Route to the right process",
        description:
          "Move confirmed deceased accounts into the correct estate or probate workflow."
      }
    ],
    useCases: [
      "Scrub collection queues before outreach",
      "Suppress deceased accounts from dialer and mail campaigns",
      "Route deceased accounts to estate and probate handling",
      "Support compliance and complaint reduction goals"
    ],
    faqs: [
      {
        question: "How do collections teams identify deceased debtors?",
        answer:
          "Collections teams batch check their account files against VerifyDeceased before outreach. Confirmed deceased accounts are suppressed from calling and mailing and routed to the appropriate estate process."
      }
    ]
  }
];

export function findSolution(slug: string): Solution | undefined {
  return SOLUTIONS.find(s => s.slug === slug);
}
